// @givmo/board — what a director's to-do list holds (CBG-UX7-WEB-DIR; design stop design-268's
// §4.2, §4.3, §4.5, §4.6 and §6.1, frames d1–d5). Its shape, and the readers it shares with the
// officer's, are `features/todo/todo-model.ts`'s; it is composed here, beside the director's pages,
// where the capacity rail keeps every director read.
//
// TWO READS THE DIRECTOR PAGES ALREADY MAKE, and one new one:
//   * the board's attention read, the retired "Waiting on you" card's source, every kind it serves:
//     the card's rows in their approved words (CBG-FIX74's, RC10's and CBG-FIX107's), and the two
//     kinds the card left to the meeting pages (a meeting to answer for, minutes to sign);
//   * the member's own getting-started list, item by item, in place of the attention read's count:
//     the new member checklist leads (Jonathan: "when they first join, the to-do list would consist of
//     the new member checklist stuff");
//   * the completed read (`GET …/attention/completed`): the seat's own acts on this board in the last
//     90 days, each in its to-do's past tense, with the checklist's own done items beside them.
// The year's questionnaire shows once: as the checklist's item where the checklist has it, else as
// the attention read's. Order (§4.3): the checklist; then what waits for a signature; then what has a
// date, soonest first; then what has none.

import type {
  BoardDirectorGettingStartedOut,
  OrgBoardDirectorAttentionOut,
} from '@givmo/api-client';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { meetingWeekday } from '../calendar/calendar-parts';
import { shortDay } from '../conflicts/coi-decode';
import { closedMinutesOfTheMeeting } from '../minutes/minutes-rule-words';
import { minutesSignaturesWords as rc10 } from '../minutes/minutes-signatures-words';
import { confirmationWords, documentName } from '../onboarding/onboarding-decode';
import {
  COMPLETED_WINDOW_MS,
  entriesOf,
  isEntry,
  meetingDay,
  newestFirst,
  text,
  timeOf,
  type DoneItem,
  type ToDoItem,
  type ToDoList,
} from '../todo/todo-model';
import { todoWords as w } from '../todo/todo-words';
import { consentsLine, waitingOn } from './director-waiting';

/** "Tue, Nov 9, 6:00 PM": a meeting's start on its own clock. */
function meetingWhen(startsAt: string, zone: string): string {
  const date = new Date(startsAt);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  let time: string;
  try {
    time = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone }).format(date);
  } catch {
    time = new Intl.DateTimeFormat('en-US', options).format(date);
  }
  return `${meetingWeekday({ starts_at: startsAt, time_zone: zone })}, ${time}`;
}

export interface DirectorReads {
  orgRef: string;
  /** The attention read as served, or undefined while it hasn't answered (or failed). */
  attention: OrgBoardDirectorAttentionOut | undefined;
  /** The member's own getting-started list; null where there is none or it hasn't answered. */
  ownList: BoardDirectorGettingStartedOut | null;
  /** The completed read as served, or undefined while it hasn't answered (or failed). */
  completed: unknown;
  /**
   * CBG-FIX107: whether one committee's consent names its committee. The page of a seat on that one
   * committee alone leaves it unsaid, as the retired card did (the page's own line names it).
   */
  committeeSaid: boolean;
  /** Now, for the checklist's done items' window when the completed read hasn't answered. */
  nowMs?: number;
}

export function directorList({
  orgRef,
  attention,
  ownList,
  completed,
  committeeSaid,
  nowMs = Date.now(),
}: DirectorReads): ToDoList {
  const search = { org: orgRef };
  const id = (identity: string) => `${orgRef}:${identity}`;
  const items: ToDoItem[] = [];

  // 1. The new member checklist, while the member's own list is open.
  let checklistCycle: string | null = null;
  if (ownList?.started === true && ownList.closed !== true) {
    for (const item of [...(ownList.items ?? [])].sort((a, b) => a.position - b.position)) {
      if (item.done === true) continue;
      if (item.item_kind === 'read') {
        if (item.available === false || !text(item.target_record_id)) continue;
        items.push({
          key: `gs-${item.id}`,
          identities: [id(`gs:${item.id}`)],
          group: 'checklist',
          line: w.readDocument(documentName(item)),
          // As the checklist page opens it: the record's page then says the board asks new
          // members to confirm it, and its way back is the checklist (CBG-YR4-START, g6).
          target: {
            to: BOARD_DIRECTOR_PAGES.record,
            params: { recordId: item.target_record_id ?? '' },
            search: { ...search, from: 'getting-started' },
          },
          count: 1,
        });
      } else if (item.item_kind === 'disclosure' && item.disclosure?.stage === 'sent') {
        const cycleId = text(item.disclosure.cycle_id);
        checklistCycle = cycleId;
        const dueOn = text(item.disclosure.due_on);
        items.push({
          key: `gs-${item.id}`,
          identities: [id(`gs:${item.id}`)],
          group: 'checklist',
          line: w.answerDisclosure(item.disclosure.label ?? ''),
          due:
            dueOn !== null && item.disclosure.joined_after_close !== true
              ? w.dueOn(shortDay(dueOn))
              : null,
          target: {
            to: BOARD_DIRECTOR_PAGES.annualDisclosure,
            search: cycleId === null ? search : { ...search, cycle: cycleId },
          },
          count: 1,
        });
      } else if (item.item_kind === 'step' && text(item.step_text) !== null) {
        items.push({
          key: `gs-${item.id}`,
          identities: [id(`gs:${item.id}`)],
          group: 'checklist',
          line: item.step_text ?? '',
          act: { kind: 'mark-done', itemId: item.id },
          count: 1,
        });
      }
    }
  }
  const hasChecklist = items.length > 0;
  const group = 'waiting' as const;
  const raw = entriesOf(attention?.items);
  const waiting = waitingOn(attention);

  // 2. What waits for a signature: the retired card's rows, in their approved words, then minutes.
  if (waiting.minutesWhileShut) {
    // Approved (CBG-FIX74): the ask email's own words; its Open brings the confirmation up.
    items.push({
      key: 'minutes-waiting',
      identities: [id('minutes-waiting')],
      group,
      line: rc10.minutesWaiting,
      act: { kind: 'open-waiting-minutes' },
      count: 1,
    });
  }
  for (const approval of waiting.approvals) {
    items.push({
      key: `rc10-${approval.recordId}`,
      identities: [id(`rc10:${approval.recordId}`)],
      group,
      // Approved (CBG-RC10): the card's line, the minutes named by their public facts alone.
      line: rc10.attentionRow(
        closedMinutesOfTheMeeting(
          approval.meetingTitle,
          meetingDay(approval.meetingStartsAt, approval.meetingTimeZone),
          approval.purposeLabel,
          { keepDateTogether: true },
        ),
      ),
      detail: approval.signedEarlierVersion ? rc10.signTheCurrentVersion : null,
      target: { to: BOARD_DIRECTOR_PAGES.record, params: { recordId: approval.recordId }, search },
      count: 1,
    });
  }
  if (waiting.consents.length > 0) {
    const [only, ...more] = waiting.consents;
    items.push({
      key: 'consents',
      identities: waiting.consents.map((consent) => id(`consent:${consent.consentId}`)),
      group,
      // Approved (CBG-FIX107): the circulation email's words, never the consent's own.
      line: consentsLine(waiting.consents, { committeeSaid }),
      target:
        only !== undefined && more.length === 0
          ? { to: BOARD_DIRECTOR_PAGES.consent, params: { consentId: only.consentId }, search }
          : { to: BOARD_DIRECTOR_PAGES.consents, search },
      count: waiting.consents.length,
    });
  }
  for (const entry of raw) {
    if (entry.kind !== 'minutes_signature') continue;
    const recordId = text(entry.reference_id);
    const meetingId = text(entry.meeting_id);
    if (recordId === null || meetingId === null) continue;
    items.push({
      key: `minutes-${recordId}`,
      identities: [id(`minutes:${recordId}`)],
      group,
      line: w.signMinutes(text(entry.title) ?? 'minutes'),
      target: { to: BOARD_DIRECTOR_PAGES.meeting, params: { meetingId }, search },
      count: 1,
    });
  }

  // 3. What has a date, soonest first: a meeting to answer for, the questionnaire, the assessment.
  const dated: { at: number; item: ToDoItem }[] = [];
  for (const entry of raw) {
    if (entry.kind !== 'meeting_attendance_intent') continue;
    const meetingId = text(entry.meeting_id);
    const startsAt = text(entry.starts_at);
    if (meetingId === null) continue;
    const zone = text(entry.time_zone) ?? 'UTC';
    const when = startsAt === null ? '' : meetingWhen(startsAt, zone);
    dated.push({
      at: (startsAt === null ? null : timeOf(startsAt)) ?? Number.MAX_SAFE_INTEGER,
      item: {
        key: `rsvp-${meetingId}`,
        identities: [id(`rsvp:${meetingId}`)],
        group,
        line: w.replyToMeeting(text(entry.title)),
        due: when.length > 0 ? w.beforeTheMeeting(when) : null,
        target: { to: BOARD_DIRECTOR_PAGES.meeting, params: { meetingId }, search },
        count: 1,
      },
    });
  }
  for (const year of waiting.years) {
    if (year.cycleId === checklistCycle) continue;
    const dueOn = year.joinedAfterClose ? null : text(year.dueOn);
    dated.push({
      at: (dueOn === null ? null : timeOf(dueOn)) ?? Number.MAX_SAFE_INTEGER,
      item: {
        key: `coi-${year.cycleId}`,
        identities: [id(`coi:${year.cycleId}`)],
        group,
        line: w.answerDisclosure(year.label),
        due: dueOn === null ? null : w.dueOn(shortDay(dueOn)),
        target: { to: BOARD_DIRECTOR_PAGES.disclosures, search },
        count: 1,
      },
    });
  }
  for (const assessment of waiting.assessments) {
    dated.push({
      at: timeOf(assessment.dueOn) ?? Number.MAX_SAFE_INTEGER,
      item: {
        key: `sa-${assessment.assessmentId}`,
        identities: [id(`sa:${assessment.assessmentId}`)],
        group,
        line: w.answerAssessment(assessment.label),
        due: w.dueOn(shortDay(assessment.dueOn)),
        target: {
          to: BOARD_DIRECTOR_PAGES.selfAssessment,
          search: { ...search, assessment: assessment.assessmentId },
        },
        count: 1,
      },
    });
  }
  dated.sort((left, right) => left.at - right.at);
  items.push(...dated.map((entry) => entry.item));

  // 4. What has no date: a copy of the return to read (the day it was provided is context).
  for (const copy of waiting.returns) {
    items.push({
      key: `return-${copy.recordId}`,
      identities: [id(`return:${copy.recordId}`)],
      group,
      line: w.readReturn(copy.namedYear, copy.form),
      detail: w.providedOn(shortDay(copy.providedOn)),
      target: { to: BOARD_DIRECTOR_PAGES.returnCopy, params: { recordId: copy.recordId }, search },
      count: 1,
    });
  }

  // EMC-WEB4 (frame 25): a Board Ask, until she has shared the campaign or started her page.
  for (const ask of waiting.asks) {
    items.push({
      key: `board-ask-${ask.askId}`,
      identities: [id(`board-ask:${ask.askId}`)],
      group,
      line: w.boardAsk(ask.campaignName, ask.offersShare, ask.offersPage),
      detail: ask.fromDisplayName === null ? null : w.boardAskFrom(ask.fromDisplayName),
      target: { to: BOARD_DIRECTOR_PAGES.ask, params: { askId: ask.askId }, search },
      count: 1,
    });
  }

  // With no checklist the list has one group, and no label (§4.5).
  if (!hasChecklist) for (const item of items) item.group = 'waiting';

  // Completed: the checklist's own done items (the member's list serves them), and the seat's acts.
  const since = isEntry(completed) ? text(completed.since) : null;
  const windowStart = (since === null ? null : timeOf(since)) ?? nowMs - COMPLETED_WINDOW_MS;
  const doneHere: DoneItem[] = [];
  for (const item of ownList?.started === true ? (ownList.items ?? []) : []) {
    if (item.item_kind === 'read' && item.confirmation != null) {
      const at = text(item.confirmation.acknowledged_at) ?? text(item.confirmation.paper_signed_on);
      if (at === null || (timeOf(at) ?? 0) < windowStart) continue;
      doneHere.push({
        key: `gs-done-${item.id}`,
        line: w.readDocument(documentName(item)),
        detail: confirmationWords(item.confirmation),
        at,
        ...(text(item.target_record_id) === null
          ? {}
          : {
              target: {
                to: BOARD_DIRECTOR_PAGES.record,
                params: { recordId: item.target_record_id ?? '' },
                search,
              },
            }),
      });
    } else if (item.item_kind === 'step' && text(item.step_done_on) !== null) {
      const at = item.step_done_on ?? '';
      if ((timeOf(at) ?? 0) < windowStart) continue;
      doneHere.push({
        key: `gs-done-${item.id}`,
        line: item.step_text ?? '',
        detail: w.doneOn(shortDay(at)),
        at,
      });
    }
  }
  return { items, completed: newestFirst([...doneHere, ...directorCompleted(completed, orgRef)]) };
}

/** The completed read's items (`GET …/attention/completed`), each in its to-do's past tense. */
export function directorCompleted(body: unknown, orgRef: string): DoneItem[] {
  if (!isEntry(body)) return [];
  const search = { org: orgRef };
  const done: DoneItem[] = [];
  for (const entry of entriesOf(body.items)) {
    const at = text(entry.completed_at);
    if (at === null) continue;
    const day = shortDay(at);
    switch (entry.kind) {
      case 'written_consent': {
        const consentId = text(entry.reference_id);
        if (consentId === null) continue;
        done.push({
          key: `consent-${consentId}`,
          line: w.signedConsent,
          // Named as its email named it: the committee, never the consent's own words (CBG-FIX107).
          which: text(entry.committee_name),
          detail: w.signedOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.consent, params: { consentId }, search },
        });
        break;
      }
      case 'minutes_signature': {
        const recordId = text(entry.reference_id);
        const meetingId = text(entry.meeting_id);
        if (recordId === null || meetingId === null) continue;
        done.push({
          key: `minutes-${recordId}`,
          line: w.signedMinutes(text(entry.title) ?? 'minutes'),
          detail: w.signedOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.meeting, params: { meetingId }, search },
        });
        break;
      }
      case 'minutes_approval_signature': {
        const recordId = text(entry.reference_id);
        if (recordId === null) continue;
        done.push({
          key: `rc10-${recordId}`,
          line: w.signedToApprove(
            closedMinutesOfTheMeeting(
              text(entry.meeting_title),
              meetingDay(text(entry.meeting_starts_at), text(entry.meeting_time_zone)),
              text(entry.purpose_label),
              { keepDateTogether: true },
            ),
          ),
          detail: w.signedOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.record, params: { recordId }, search },
        });
        break;
      }
      case 'meeting_attendance_intent': {
        const meetingId = text(entry.meeting_id);
        if (meetingId === null) continue;
        const line =
          entry.intent === 'attending'
            ? w.saidAttending
            : entry.intent === 'not_attending'
              ? w.saidNotAttending
              : entry.intent === 'tentative'
                ? w.saidMaybe
                : null;
        if (line === null) continue;
        done.push({
          key: `rsvp-${meetingId}`,
          line,
          which: text(entry.title),
          detail: w.answeredOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.meeting, params: { meetingId }, search },
        });
        break;
      }
      case 'coi_disclosure': {
        const cycleId = text(entry.cycle_id);
        if (cycleId === null) continue;
        done.push({
          key: `coi-${cycleId}`,
          line: w.answeredDisclosure(text(entry.label) ?? ''),
          // CBG-UX-COPY3's D12: the list's own word for a form filled out; an RSVP keeps "Answered".
          detail: w.doneOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.disclosures, search },
        });
        break;
      }
      case 'self_assessment': {
        const assessmentId = text(entry.assessment_id);
        if (assessmentId === null) continue;
        done.push({
          key: `sa-${assessmentId}`,
          line: w.answeredAssessment(text(entry.label) ?? ''),
          detail: w.answeredOn(day),
          at,
          target: {
            to: BOARD_DIRECTOR_PAGES.selfAssessment,
            search: { ...search, assessment: assessmentId },
          },
        });
        break;
      }
      case 'return_to_read': {
        const recordId = text(entry.record_id);
        if (recordId === null || typeof entry.named_year !== 'number') continue;
        done.push({
          key: `return-${recordId}`,
          line: w.readTheReturn(entry.named_year, text(entry.form) ?? '990'),
          detail: w.doneOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.returnCopy, params: { recordId }, search },
        });
        break;
      }
      case 'board_ask': {
        const askId = text(entry.ask_id);
        if (askId === null) continue;
        done.push({
          key: `board-ask-${askId}`,
          line: w.actedOnBoardAsk(text(entry.campaign_name) ?? ''),
          detail: w.doneOn(day),
          at,
          target: { to: BOARD_DIRECTOR_PAGES.ask, params: { askId }, search },
        });
        break;
      }
      default:
        // A newer backend's kind: left out, never a half-row.
        break;
    }
  }
  return done;
}
